/* =============================================================================
   PRODUCT-FORM.JS — StylishRugs Theme
   Product form: variant selection, add to cart, inventory, price updates
   ============================================================================= */

'use strict';

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.variantIdInput = this.querySelector('[name="id"]');
    this.submitBtn = this.querySelector('[data-add-to-cart]');
    this.priceEl = this.querySelector('[data-product-price]');
    this.comparePriceEl = this.querySelector('[data-compare-price]');
    this.stockEl = this.querySelector('[data-stock-message]');
    this.variantSelects = this.querySelectorAll('[data-variant-select]');
    this.variantButtons = this.querySelectorAll('[data-variant-button-group]');
    this.productData = this._parseProductData();

    if (!this.form || !this.productData) return;

    this.form.addEventListener('submit', (e) => this.handleSubmit(e));

    // Listen to variant option changes
    this.variantSelects.forEach((select) => {
      select.addEventListener('change', () => this.onVariantChange());
    });

    this.querySelectorAll('[data-variant-option]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const group = btn.closest('[data-variant-button-group]');
        group?.querySelectorAll('[data-variant-option]').forEach((b) => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        this.onVariantChange();
      });
    });

    // Set initial state
    this.onVariantChange();
  }

  _parseProductData() {
    const el = this.querySelector('[data-product-json]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); }
    catch (e) { return null; }
  }

  getCurrentOptions() {
    const options = [];

    // From selects
    this.variantSelects.forEach((select) => {
      options.push(select.value);
    });

    // From button groups
    this.variantButtons.forEach((group) => {
      const selected = group.querySelector('[data-variant-option].is-selected');
      if (selected) options.push(selected.dataset.value);
    });

    return options;
  }

  findVariant(options) {
    if (!this.productData?.variants) return null;
    return this.productData.variants.find((v) => {
      return options.every((opt, i) => v.options[i] === opt || opt === undefined);
    }) || null;
  }

  onVariantChange() {
    const options = this.getCurrentOptions();
    const variant = this.findVariant(options);
    this.updateUI(variant);

    // Sync hidden id input
    if (this.variantIdInput) {
      this.variantIdInput.value = variant ? variant.id : '';
    }

    // Update URL without reload
    if (variant) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }
  }

  updateUI(variant) {
    const btn = this.submitBtn;

    if (!variant) {
      if (btn) {
        btn.disabled = true;
        btn.textContent = 'Unavailable';
      }
      return;
    }

    // Price
    if (this.priceEl) {
      this.priceEl.textContent = formatMoney(variant.price);
    }
    if (this.comparePriceEl) {
      if (variant.compare_at_price > variant.price) {
        this.comparePriceEl.textContent = formatMoney(variant.compare_at_price);
        this.comparePriceEl.hidden = false;
      } else {
        this.comparePriceEl.hidden = true;
      }
    }

    // Stock / ATC button
    if (btn) {
      if (!variant.available) {
        btn.disabled = true;
        btn.textContent = 'Sold Out';
      } else {
        btn.disabled = false;
        btn.textContent = btn.dataset.addLabel || 'Add to Cart';
      }
    }

    // Stock message
    if (this.stockEl) {
      if (variant.inventory_management && variant.inventory_quantity <= 5 && variant.inventory_quantity > 0) {
        this.stockEl.textContent = `Only ${variant.inventory_quantity} left in stock`;
        this.stockEl.hidden = false;
      } else if (!variant.available) {
        this.stockEl.textContent = 'Out of stock';
        this.stockEl.hidden = false;
      } else {
        this.stockEl.hidden = true;
      }
    }

    // Featured image swap
    if (variant.featured_image) {
      const gallery = document.querySelector('product-media-gallery');
      if (gallery) {
        const thumb = gallery.querySelector(`[data-thumbnail][data-media-id="${variant.featured_image.id}"]`);
        if (thumb) {
          const index = Array.from(gallery.querySelectorAll('[data-thumbnail]')).indexOf(thumb);
          gallery.switchImage(index);
        }
      }
    }
  }

  async handleSubmit(e) {
    e.preventDefault();
    const btn = this.submitBtn;
    if (btn?.disabled) return;

    const id = this.variantIdInput?.value;
    if (!id) return;

    const quantity = parseInt(
      this.querySelector('[data-quantity-input]')?.value || '1',
      10
    );

    if (btn) {
      btn.classList.add('button--loading');
      btn.disabled = true;
    }

    try {
      const item = await CartAPI.addItem({ id: parseInt(id, 10), quantity });
      const cart = await CartAPI.getCart();
      PubSub.publish(EVENTS.CART_UPDATE, cart);
      PubSub.publish(EVENTS.PRODUCT_ADDED, item);
      PubSub.publish(EVENTS.CART_OPEN);
    } catch (err) {
      this.showError(err.message || 'Something went wrong. Please try again.');
    } finally {
      if (btn) {
        btn.classList.remove('button--loading');
        btn.disabled = false;
      }
    }
  }

  showError(message) {
    let errEl = this.querySelector('[data-form-error]');
    if (!errEl) {
      errEl = document.createElement('p');
      errEl.setAttribute('data-form-error', '');
      errEl.style.cssText = 'color:red;font-size:1.3rem;margin-top:0.8rem;';
      this.form.appendChild(errEl);
    }
    errEl.textContent = message;
    setTimeout(() => { errEl.textContent = ''; }, 5000);
  }
}

customElements.define('product-form', ProductForm);
